import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, CheckCircle2, AlertCircle, ExternalLink, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBilling } from '../contexts/BillingContext';
import { supabase } from '../lib/supabase';
import '../styles/BillingSettings.css';

const UNIT_PRICE_USD = 0.03;

interface UsageHistoryEntry {
  action_id: string;
  estimation_id: string;
  units: number;
  status: string;
  created_at: string;
  committed_at: string | null;
  project_name: string;
}

interface UsageAggregateEntry {
  units: number;
  committed_at: string | null;
  created_at: string | null;
}

type UsageTimeSpan = '7d' | '30d' | '90d' | '365d' | 'all' | 'custom';
type UsageBucket = 'day' | 'week' | 'month';

interface UsageChartPoint {
  label: string;
  units: number;
}

interface UsageChartCoord {
  x: number;
  y: number;
  units: number;
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const timeSpanOptions: Array<{ value: UsageTimeSpan; label: string }> = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: '365d', label: '1Y' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

const toDateInputValue = (date: Date) => {
  const adjusted = new Date(date);
  adjusted.setMinutes(adjusted.getMinutes() - adjusted.getTimezoneOffset());
  return adjusted.toISOString().slice(0, 10);
};

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfWeek = (date: Date) => {
  const d = startOfDay(date);
  const day = d.getDay();
  const daysSinceMonday = (day + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday);
  return d;
};

const startOfMonth = (date: Date) => {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
};

const getBucketStart = (date: Date, bucket: UsageBucket) => {
  if (bucket === 'day') return startOfDay(date);
  if (bucket === 'week') return startOfWeek(date);
  return startOfMonth(date);
};

const incrementBucket = (date: Date, bucket: UsageBucket) => {
  const next = new Date(date);
  if (bucket === 'day') {
    next.setDate(next.getDate() + 1);
    return next;
  }
  if (bucket === 'week') {
    next.setDate(next.getDate() + 7);
    return next;
  }
  next.setMonth(next.getMonth() + 1);
  return next;
};

const formatBucketLabel = (date: Date, bucket: UsageBucket) => {
  if (bucket === 'day') {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  }
  if (bucket === 'week') {
    return `Wk ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date)}`;
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' }).format(date);
};

const getLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BillingSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasActiveBilling, refreshBillingStatus } = useBilling();
  const [statusLoading, setStatusLoading] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [dueLoading, setDueLoading] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryEntry[]>([]);
  const [allTimeTotalDetections, setAllTimeTotalDetections] = useState(0);
  const [allCommittedUsageRows, setAllCommittedUsageRows] = useState<UsageAggregateEntry[]>([]);
  const [usageTimeSpan, setUsageTimeSpan] = useState<UsageTimeSpan>('30d');
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);
  const [customStartDate, setCustomStartDate] = useState<string>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return toDateInputValue(start);
  });
  const [customEndDate, setCustomEndDate] = useState<string>(() => toDateInputValue(new Date()));
  const [dueThisFridayCents, setDueThisFridayCents] = useState<number | null>(null);
  const [dueCurrency, setDueCurrency] = useState('usd');
  const [error, setError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [dueError, setDueError] = useState<string | null>(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [billingSetupLaunching, setBillingSetupLaunching] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [billingSetupUrl, setBillingSetupUrl] = useState<string | null>(null);
  const popupPollRef = useRef<number | null>(null);

  useEffect(() => {
    if (user) {
      void refreshBillingStatus();
      fetchBillingStatus();
      fetchUsageHistory();
      fetchDueThisFriday();
    }
  }, [user, refreshBillingStatus]);

  useEffect(() => {
    if (!hasActiveBilling) return;

    try {
      const shouldReturnToHome = sessionStorage.getItem('bidflow_post_login') === '1';
      if (shouldReturnToHome) {
        sessionStorage.removeItem('bidflow_post_login');
        navigate('/', { replace: true });
      }
    } catch {
      // Ignore storage failures and keep user on this page.
    }
  }, [hasActiveBilling, navigate]);

  useEffect(() => {
    if (!user?.id) {
      setShowSetupModal(false);
      setPopupBlocked(false);
      setBillingSetupUrl(null);
      return;
    }

    let shouldPromptForSetup = false;
    try {
      shouldPromptForSetup = sessionStorage.getItem('bidflow_post_login') === '1';
    } catch {
      shouldPromptForSetup = false;
    }

    if (!hasActiveBilling && shouldPromptForSetup) {
      setShowSetupModal(true);
    } else if (hasActiveBilling) {
      setShowSetupModal(false);
      setPopupBlocked(false);
      setBillingSetupUrl(null);
    }
  }, [user?.id, hasActiveBilling]);

  useEffect(() => {
    if (!showSetupModal) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showSetupModal]);

  useEffect(() => {
    return () => {
      if (popupPollRef.current !== null) {
        window.clearInterval(popupPollRef.current);
        popupPollRef.current = null;
      }
    };
  }, []);

  const fetchBillingStatus = async () => {
    try {
      setStatusLoading(true);
      // Using the billing_profiles table as defined in your schema
      const { data, error } = await supabase
        .from('billing_profiles')
        .select('stripe_customer_id, status')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching billing status:', error);
      } else {
        // Map 'active' status to has_payment_method for the UI
        setCustomerData({
          ...data,
          has_payment_method: data?.status === 'active'
        });
      }
    } catch (err) {
      console.error('Failed to fetch billing status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchUsageHistory = async () => {
    if (!user?.id) return;

    try {
      setUsageLoading(true);
      setUsageError(null);

      const { data: usageRows, error: usageRowsError } = await supabase
        .from('billing_usage_events')
        .select('action_id, estimation_id, units, status, created_at, committed_at')
        .eq('user_id', user.id)
        .eq('status', 'committed')
        .order('committed_at', { ascending: false })
        .limit(50);

      if (usageRowsError) {
        throw usageRowsError;
      }

      const rows = usageRows || [];
      if (rows.length === 0) {
        setUsageHistory([]);
        setAllTimeTotalDetections(0);
        setAllCommittedUsageRows([]);
        return;
      }

      const estimationIds = Array.from(
        new Set(rows.map((row: any) => row.estimation_id).filter(Boolean))
      );

      const projectNameById = new Map<string, string>();

      if (estimationIds.length > 0) {
        const { data: estimationRows, error: estimationRowsError } = await supabase
          .from('estimations')
          .select('id, name')
          .in('id', estimationIds);

        if (estimationRowsError) {
          throw estimationRowsError;
        }

        (estimationRows || []).forEach((estimation: any) => {
          if (estimation?.id) {
            projectNameById.set(estimation.id, estimation.name || 'Untitled Project');
          }
        });
      }

      const mappedRows: UsageHistoryEntry[] = rows.map((row: any) => ({
        action_id: row.action_id,
        estimation_id: row.estimation_id,
        units: Number(row.units) || 0,
        status: row.status || 'unknown',
        created_at: row.created_at,
        committed_at: row.committed_at,
        project_name: projectNameById.get(row.estimation_id) || 'Unknown Project',
      }));

      setUsageHistory(mappedRows);

      // Compute a true all-time total by paging through every committed usage row.
      // We keep the table itself capped for UI performance.
      try {
        const PAGE_SIZE = 1000;
        let from = 0;
        let totalUnits = 0;
        const aggregateRows: UsageAggregateEntry[] = [];

        while (true) {
          const { data: unitsRows, error: unitsError } = await supabase
            .from('billing_usage_events')
            .select('units, committed_at, created_at, action_id')
            .eq('user_id', user.id)
            .eq('status', 'committed')
            .order('action_id', { ascending: true })
            .range(from, from + PAGE_SIZE - 1);

          if (unitsError) {
            throw unitsError;
          }

          const currentRows = unitsRows || [];
          const normalizedRows: UsageAggregateEntry[] = currentRows.map((row: any) => ({
            units: Number(row.units) || 0,
            committed_at: row.committed_at || null,
            created_at: row.created_at || null,
          }));

          aggregateRows.push(...normalizedRows);
          totalUnits += normalizedRows.reduce((sum, row) => sum + row.units, 0);

          if (currentRows.length < PAGE_SIZE) {
            break;
          }

          from += PAGE_SIZE;
        }

        setAllCommittedUsageRows(aggregateRows);
        setAllTimeTotalDetections(totalUnits);
      } catch (totalsErr) {
        console.error('Failed to fetch all-time usage totals, falling back to visible rows:', totalsErr);
        setAllCommittedUsageRows(
          mappedRows.map((row) => ({
            units: row.units,
            committed_at: row.committed_at,
            created_at: row.created_at,
          }))
        );
        setAllTimeTotalDetections(mappedRows.reduce((sum, entry) => sum + entry.units, 0));
      }
    } catch (err: any) {
      console.error('Failed to fetch usage history:', err);
      setUsageError(err.message || 'Could not load usage history.');
      setAllCommittedUsageRows([]);
      setAllTimeTotalDetections(0);
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchDueThisFriday = async () => {
    if (!user?.id) return;

    try {
      setDueLoading(true);
      setDueError(null);

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        setDueThisFridayCents(null);
        return;
      }

      const response = await fetch('/api/billing/upcoming-due', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to load due amount (${response.status})`);
      }

      const active = !!payload?.hasActiveBilling;
      setDueCurrency(String(payload?.currency || 'usd').toLowerCase());
      setDueThisFridayCents(active ? Number(payload?.dueAmountCents ?? 0) : null);
    } catch (err: any) {
      console.error('Failed to fetch due amount preview:', err);
      setDueError(err.message || 'Could not load due amount.');
      setDueThisFridayCents(null);
    } finally {
      setDueLoading(false);
    }
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown time';

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatPrice = (units: number) => `$${(units * UNIT_PRICE_USD).toFixed(2)}`;
  const formatCurrencyFromCents = (amountCents: number, currency: string) => {
    const safeAmount = Number.isFinite(amountCents) ? amountCents : 0;
    const safeCurrency = (currency || 'usd').toUpperCase();

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: safeCurrency,
      }).format(safeAmount / 100);
    } catch {
      return `$${(safeAmount / 100).toFixed(2)}`;
    }
  };

  const dueThisFridayDisplay = dueLoading
    ? 'Loading...'
    : hasActiveBilling && dueThisFridayCents !== null
    ? formatCurrencyFromCents(dueThisFridayCents, dueCurrency)
    : 'Add payment method';

  const billingStatusVariant = statusLoading && !customerData
    ? 'loading'
    : hasActiveBilling
    ? 'connected'
    : 'disconnected';
  const billingStatusLabel = statusLoading && !customerData
    ? 'Checking billing'
    : hasActiveBilling
    ? 'Card Connected'
    : 'Card Required';
  const billingStatusDescription = hasActiveBilling
    ? 'Your payment method is active and ready for metered usage.'
    : 'Add a payment method to keep usage reporting and billing in sync.';

  const allTimeTotal = allTimeTotalDetections * UNIT_PRICE_USD;
  const formatCompactUnits = (value: number) =>
    new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: value >= 1000 ? 1 : 0,
    }).format(value);

  const usageTrend = useMemo(() => {
    const parsedRows = allCommittedUsageRows
      .map((row) => {
        const rawTimestamp = row.committed_at || row.created_at;
        if (!rawTimestamp) return null;
        const timestamp = new Date(rawTimestamp);
        if (Number.isNaN(timestamp.getTime())) return null;
        return { timestamp, units: row.units };
      })
      .filter((row): row is { timestamp: Date; units: number } => !!row);

    const now = new Date();
    const defaultEnd = endOfDay(now);
    const defaultStart = startOfDay(new Date(defaultEnd.getTime() - (29 * DAY_MS)));

    let rangeStart = defaultStart;
    let rangeEnd = defaultEnd;

    if (usageTimeSpan === 'custom') {
      const parsedStart = customStartDate ? new Date(`${customStartDate}T00:00:00`) : null;
      const parsedEnd = customEndDate ? new Date(`${customEndDate}T23:59:59.999`) : null;

      if (parsedStart && !Number.isNaN(parsedStart.getTime())) {
        rangeStart = startOfDay(parsedStart);
      }
      if (parsedEnd && !Number.isNaN(parsedEnd.getTime())) {
        rangeEnd = endOfDay(parsedEnd);
      }
      if (rangeStart > rangeEnd) {
        const originalStart = rangeStart;
        rangeStart = startOfDay(rangeEnd);
        rangeEnd = endOfDay(originalStart);
      }
    } else if (usageTimeSpan === 'all') {
      if (parsedRows.length > 0) {
        const oldestTimestamp = parsedRows.reduce((min, row) =>
          row.timestamp.getTime() < min.getTime() ? row.timestamp : min
        , parsedRows[0].timestamp);
        rangeStart = startOfDay(oldestTimestamp);
        rangeEnd = defaultEnd;
      }
    } else {
      const days = usageTimeSpan === '7d' ? 7 : usageTimeSpan === '30d' ? 30 : usageTimeSpan === '90d' ? 90 : 365;
      rangeEnd = defaultEnd;
      rangeStart = startOfDay(new Date(rangeEnd.getTime() - ((days - 1) * DAY_MS)));
    }

    const inRangeRows = parsedRows.filter(
      (row) => row.timestamp.getTime() >= rangeStart.getTime() && row.timestamp.getTime() <= rangeEnd.getTime()
    );

    const spanDays = Math.max(1, Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS) + 1);
    const bucket: UsageBucket = spanDays <= 45 ? 'day' : spanDays <= 220 ? 'week' : 'month';

    const bucketTotals = new Map<string, number>();
    inRangeRows.forEach((row) => {
      const bucketStart = getBucketStart(row.timestamp, bucket);
      const bucketKey = getLocalDateKey(bucketStart);
      bucketTotals.set(bucketKey, (bucketTotals.get(bucketKey) || 0) + row.units);
    });

    const points: UsageChartPoint[] = [];
    let cursor = getBucketStart(rangeStart, bucket);
    let guard = 0;
    while (cursor.getTime() <= rangeEnd.getTime() && guard < 500) {
      const key = getLocalDateKey(cursor);
      points.push({
        label: formatBucketLabel(cursor, bucket),
        units: bucketTotals.get(key) || 0,
      });
      cursor = incrementBucket(cursor, bucket);
      guard += 1;
    }

    const inRangeUnits = inRangeRows.reduce((sum, row) => sum + row.units, 0);

    return {
      points,
      inRangeUnits,
      inRangeEvents: inRangeRows.length,
    };
  }, [allCommittedUsageRows, usageTimeSpan, customStartDate, customEndDate]);

  const chartGeometry = useMemo(() => {
    if (usageTrend.points.length === 0) return null;

    const width = 900;
    const height = 320;
    const paddingLeft = 54;
    const paddingRight = 18;
    const paddingTop = 18;
    const paddingBottom = 46;

    const innerWidth = width - paddingLeft - paddingRight;
    const innerHeight = height - paddingTop - paddingBottom;
    const maxUnitsRaw = Math.max(...usageTrend.points.map((point) => point.units), 1);
    const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(maxUnitsRaw)));
    const normalized = maxUnitsRaw / magnitude;
    const niceMax = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;

    const baselineY = paddingTop + innerHeight;
    const stepX = usageTrend.points.length > 1 ? innerWidth / (usageTrend.points.length - 1) : 0;

    const coords: UsageChartCoord[] = usageTrend.points.map((point, index) => {
      const x = usageTrend.points.length === 1 ? paddingLeft + innerWidth / 2 : paddingLeft + (index * stepX);
      const y = baselineY - ((point.units / niceMax) * innerHeight);
      return { x, y, units: point.units, label: point.label };
    });

    const linePath = coords.map((point, index) =>
      `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    ).join(' ');

    const areaPath = coords.length > 0
      ? `${linePath} L ${coords[coords.length - 1].x.toFixed(2)} ${baselineY.toFixed(2)} L ${coords[0].x.toFixed(2)} ${baselineY.toFixed(2)} Z`
      : '';

    const yTicks = [0, niceMax / 2, niceMax];
    const xLabelStep = Math.max(1, Math.ceil(coords.length / 7));
    const xLabels = coords.filter((_, index) => index % xLabelStep === 0 || index === coords.length - 1);

    return {
      width,
      height,
      paddingLeft,
      paddingRight,
      paddingTop,
      baselineY,
      coords,
      linePath,
      areaPath,
      yTicks,
      xLabels,
    };
  }, [usageTrend.points]);

  const hoveredPoint = useMemo(() => {
    if (!chartGeometry || hoveredPointIndex === null) return null;
    return chartGeometry.coords[hoveredPointIndex] || null;
  }, [chartGeometry, hoveredPointIndex]);

  const tooltipGeometry = useMemo(() => {
    if (!chartGeometry || !hoveredPoint) return null;

    const tooltipWidth = 176;
    const tooltipHeight = 58;
    const margin = 10;
    const pointerGap = 12;

    let x = hoveredPoint.x + pointerGap;
    if (x + tooltipWidth > chartGeometry.width - margin) {
      x = hoveredPoint.x - tooltipWidth - pointerGap;
    }
    x = Math.max(margin, Math.min(x, chartGeometry.width - tooltipWidth - margin));

    let y = hoveredPoint.y - tooltipHeight - pointerGap;
    if (y < margin) {
      y = hoveredPoint.y + pointerGap;
    }
    y = Math.max(margin, Math.min(y, chartGeometry.height - tooltipHeight - margin));

    return { x, y, width: tooltipWidth, height: tooltipHeight };
  }, [chartGeometry, hoveredPoint]);

  const inRangeCost = usageTrend.inRangeUnits * UNIT_PRICE_USD;

  const requestBillingSessionUrl = async () => {
    const response = await fetch('/api/billing/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user?.id,
        email: user?.email,
        returnUrl: window.location.href,
      }),
    });

    const data = await response.json();
    if (!data?.url) {
      throw new Error(data?.error || 'Failed to create billing session');
    }

    return String(data.url);
  };

  const handleManageBilling = async () => {
    try {
      setManagingBilling(true);
      setError(null);

      const url = await requestBillingSessionUrl();
      window.location.href = url;
    } catch (err: any) {
      console.error('Billing redirect error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setManagingBilling(false);
    }
  };

  const handleSetupBillingFromModal = async () => {
    let openedPopup = false;
    try {
      setBillingSetupLaunching(true);
      setPopupBlocked(false);
      setError(null);

      const url = await requestBillingSessionUrl();
      setBillingSetupUrl(url);

      const popup = window.open(
        url,
        'bidflow-billing-setup',
        'popup=yes,width=520,height=760,resizable=yes,scrollbars=yes'
      );

      if (!popup) {
        setPopupBlocked(true);
        return;
      }

      openedPopup = true;
      popup.focus();

      if (popupPollRef.current !== null) {
        window.clearInterval(popupPollRef.current);
      }

      popupPollRef.current = window.setInterval(() => {
        if (!popup.closed) return;

        if (popupPollRef.current !== null) {
          window.clearInterval(popupPollRef.current);
          popupPollRef.current = null;
        }

        void (async () => {
          const active = await refreshBillingStatus();
          setBillingSetupLaunching(false);
          if (active) {
            try {
              sessionStorage.removeItem('bidflow_post_login');
            } catch {
              // Ignore storage failures.
            }
            navigate('/', { replace: true });
            return;
          }

          setShowSetupModal(true);
        })();
      }, 500);
    } catch (err: any) {
      console.error('Billing setup popup error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      if (!openedPopup) {
        setBillingSetupLaunching(false);
      }
    }
  };

  const handleOpenBillingFallback = () => {
    if (!billingSetupUrl) return;
    window.location.href = billingSetupUrl;
  };

  return (
    <div className="billing-settings-container">
      <div className="billing-header">
        <h1>Billing & Usage</h1>
      </div>

      <div className="billing-content">
        <div className="billing-card billing-overview-card">
          <div className="billing-overview-header">
            <div className="billing-overview-title-group">
              <div className="billing-overview-title-row">
                <span className="billing-overview-icon">
                  <CreditCard size={18} />
                </span>
                <h2>Payment Method</h2>
              </div>
              <p>Manage your usage-based billing details and Stripe payment setup.</p>
            </div>
            <div className={`billing-status-chip ${billingStatusVariant}`}>
              {billingStatusVariant === 'loading' ? (
                <Loader2 size={14} className="spinner" />
              ) : billingStatusVariant === 'connected' ? (
                <CheckCircle2 size={14} />
              ) : (
                <AlertCircle size={14} />
              )}
              <span>{billingStatusLabel}</span>
            </div>
          </div>

          <div className="billing-overview-stats-grid">
            <div className="billing-overview-stat">
              <span className="billing-overview-stat-label">Due This Friday</span>
              <strong className="billing-overview-stat-value">{dueThisFridayDisplay}</strong>
              {dueError && <span className="billing-overview-stat-note">{dueError}</span>}
            </div>
            <div className="billing-overview-stat">
              <span className="billing-overview-stat-label">Usage Rate</span>
              <strong className="billing-overview-stat-value">$0.03 / detection</strong>
              <span className="billing-overview-stat-note">Metered weekly on approved usage.</span>
            </div>
          </div>

          <div className="billing-overview-footer">
            <p className="billing-overview-description">{billingStatusDescription}</p>
            <button
              className="manage-billing-btn"
              onClick={handleManageBilling}
              disabled={managingBilling}
            >
              {managingBilling ? (
                <Loader2 className="spinner" size={18} />
              ) : (
                <>
                  <span>{hasActiveBilling ? 'Manage Payment Method' : 'Add Payment Method'}</span>
                  <ExternalLink size={16} />
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>

      <div className="billing-card usage-section">
        <div className="usage-section-header">
          <h3>Usage History</h3>
          <button className="text-btn" onClick={handleManageBilling} disabled={managingBilling}>
            View Invoices
          </button>
        </div>

        <div className="usage-chart-card">
          <div className="usage-chart-header">
            <div className="usage-chart-title-group">
              <h4>Usage Trend</h4>
              <p>Track qualified detections across custom time spans.</p>
            </div>
            <div className="usage-span-controls" role="tablist" aria-label="Usage timespan selector">
              {timeSpanOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={usageTimeSpan === option.value}
                  className={`usage-span-btn ${usageTimeSpan === option.value ? 'active' : ''}`}
                  onClick={() => setUsageTimeSpan(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {usageTimeSpan === 'custom' && (
            <div className="usage-custom-range-row">
              <label className="usage-date-field">
                <span>From</span>
                <input
                  type="date"
                  className="usage-date-input"
                  value={customStartDate}
                  onChange={(event) => setCustomStartDate(event.target.value)}
                  max={customEndDate || undefined}
                />
              </label>
              <label className="usage-date-field">
                <span>To</span>
                <input
                  type="date"
                  className="usage-date-input"
                  value={customEndDate}
                  onChange={(event) => setCustomEndDate(event.target.value)}
                  min={customStartDate || undefined}
                  max={toDateInputValue(new Date())}
                />
              </label>
            </div>
          )}

          <div className="usage-chart-metrics">
            <div className="usage-chart-metric">
              <span>In Range Detections</span>
              <strong>{usageTrend.inRangeUnits.toLocaleString()}</strong>
            </div>
            <div className="usage-chart-metric">
              <span>In Range Cost</span>
              <strong>${inRangeCost.toFixed(2)}</strong>
            </div>
            <div className="usage-chart-metric">
              <span>Committed Events</span>
              <strong>{usageTrend.inRangeEvents.toLocaleString()}</strong>
            </div>
          </div>

          <div className="usage-chart-shell">
            {usageLoading ? (
              <div className="billing-loading usage-loading">
                <Loader2 className="spinner" size={18} />
                <span>Preparing usage trend...</span>
              </div>
            ) : usageError ? (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{usageError}</span>
              </div>
            ) : !chartGeometry || usageTrend.points.length === 0 ? (
              <div className="usage-chart-empty">
                <p>No committed usage events found for this time span.</p>
              </div>
            ) : (
              <svg
                className="usage-chart-svg"
                viewBox={`0 0 ${chartGeometry.width} ${chartGeometry.height}`}
                preserveAspectRatio="xMidYMid meet"
                aria-label="Usage trend chart"
                onMouseLeave={() => setHoveredPointIndex(null)}
              >
                <defs>
                  <linearGradient id="usageAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.02" />
                  </linearGradient>
                  <linearGradient id="usageLineGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#0891b2" />
                    <stop offset="50%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#22d3ee" />
                  </linearGradient>
                </defs>

                {chartGeometry.yTicks.map((tick, idx) => {
                  const maxTick = chartGeometry.yTicks[chartGeometry.yTicks.length - 1] || 1;
                  const y = chartGeometry.baselineY - ((tick / maxTick) * (chartGeometry.baselineY - chartGeometry.paddingTop));
                  return (
                    <g key={`tick-${idx}`}>
                      <line
                        x1={chartGeometry.paddingLeft}
                        y1={y}
                        x2={chartGeometry.width - chartGeometry.paddingRight}
                        y2={y}
                        className="usage-chart-grid-line"
                      />
                      <text
                        x={chartGeometry.paddingLeft - 10}
                        y={y + 4}
                        textAnchor="end"
                        className="usage-chart-y-label"
                      >
                        {formatCompactUnits(tick)}
                      </text>
                    </g>
                  );
                })}

                <path className="usage-chart-area" d={chartGeometry.areaPath} />
                <path className="usage-chart-line" d={chartGeometry.linePath} />

                {chartGeometry.coords.map((point, index) => {
                  const isHovered = hoveredPointIndex === index;

                  return (
                    <g
                      key={`point-${index}`}
                      onMouseEnter={() => setHoveredPointIndex(index)}
                      onMouseLeave={() =>
                        setHoveredPointIndex((prev) => (prev === index ? null : prev))
                      }
                    >
                      <title>{`${point.label}: ${point.units.toLocaleString()} detections ($${(point.units * UNIT_PRICE_USD).toFixed(2)})`}</title>
                      <line
                        x1={point.x}
                        y1={chartGeometry.baselineY}
                        x2={point.x}
                        y2={point.y}
                        className={`usage-chart-stem ${isHovered ? 'active' : ''}`}
                      />
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={11}
                        className="usage-chart-hit-target"
                      />
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={isHovered ? 5.2 : 4}
                        className={`usage-chart-point ${isHovered ? 'active' : ''}`}
                      />
                    </g>
                  );
                })}

                {hoveredPoint && tooltipGeometry && (
                  <g className="usage-chart-tooltip" pointerEvents="none">
                    <rect
                      x={tooltipGeometry.x}
                      y={tooltipGeometry.y}
                      width={tooltipGeometry.width}
                      height={tooltipGeometry.height}
                      rx={9}
                      className="usage-chart-tooltip-box"
                    />
                    <text
                      x={tooltipGeometry.x + 10}
                      y={tooltipGeometry.y + 17}
                      className="usage-chart-tooltip-label"
                    >
                      {hoveredPoint.label}
                    </text>
                    <text
                      x={tooltipGeometry.x + 10}
                      y={tooltipGeometry.y + 35}
                      className="usage-chart-tooltip-value"
                    >
                      {hoveredPoint.units.toLocaleString()} detections
                    </text>
                    <text
                      x={tooltipGeometry.x + 10}
                      y={tooltipGeometry.y + 51}
                      className="usage-chart-tooltip-cost"
                    >
                      ${(hoveredPoint.units * UNIT_PRICE_USD).toFixed(2)}
                    </text>
                  </g>
                )}

                {chartGeometry.xLabels.map((point, index) => (
                  <text
                    key={`label-${index}`}
                    x={point.x}
                    y={chartGeometry.height - 16}
                    textAnchor="middle"
                    className="usage-chart-x-label"
                  >
                    {point.label}
                  </text>
                ))}
              </svg>
            )}
          </div>
        </div>

        <div className="usage-summary-grid">
          <div className="usage-summary-item">
            <span className="usage-summary-label">All Time Total</span>
            <strong className="usage-summary-value">${allTimeTotal.toFixed(2)}</strong>
          </div>
          <div className="usage-summary-item">
            <span className="usage-summary-label">All Time Total Detections</span>
            <strong className="usage-summary-value">{allTimeTotalDetections}</strong>
          </div>
          <div className="usage-summary-item">
            <span className="usage-summary-label">Flat Rate</span>
            <strong className="usage-summary-value">$0.03 / detection</strong>
          </div>
        </div>

        <div className="usage-table-wrapper">
          <table className="usage-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Timestamp</th>
                <th>Qualified Detections</th>
                <th>Final Price</th>
              </tr>
            </thead>
            <tbody>
              {usageLoading ? (
                <tr>
                  <td colSpan={4}>
                    <div className="billing-loading usage-loading">
                      <Loader2 className="spinner" size={18} />
                      <span>Loading usage history...</span>
                    </div>
                  </td>
                </tr>
              ) : usageError ? (
                <tr>
                  <td colSpan={4}>
                    <div className="error-message">
                      <AlertCircle size={16} />
                      <span>{usageError}</span>
                    </div>
                  </td>
                </tr>
              ) : usageHistory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="usage-empty">
                    No committed usage yet. Once you finish and save reviewed detections, usage will appear here.
                  </td>
                </tr>
              ) : (
                usageHistory.map((entry) => (
                  <tr key={entry.action_id}>
                    <td>{entry.project_name}</td>
                    <td>{formatTimestamp(entry.committed_at || entry.created_at)}</td>
                    <td>{entry.units}</td>
                    <td>{entry.units} x $0.03 = {formatPrice(entry.units)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showSetupModal && !hasActiveBilling && (
        <div
          className="billing-setup-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-setup-modal-title"
        >
          <div className="billing-setup-modal">
            <div className="billing-setup-brand-icons" aria-hidden="true">
              <span className="billing-setup-card-icon">
                <CreditCard size={96} />
                <span className="billing-setup-card-badge">
                  <img
                    src="/bidflowlogo-removebg-preview.png"
                    alt=""
                    className="billing-setup-card-badge-logo"
                  />
                </span>
              </span>
            </div>
            <h2 id="billing-setup-modal-title">Connect your card</h2>
            <p>
              You need to connect your card in order to continue.
            </p>

            <button
              className="billing-setup-primary-btn"
              onClick={handleSetupBillingFromModal}
              disabled={billingSetupLaunching}
            >
              {billingSetupLaunching ? (
                <span className="billing-setup-primary-loading">
                  <Loader2 className="spinner" size={18} />
                  <span>Connecting...</span>
                </span>
              ) : (
                <>
                  <span className="billing-setup-primary-label">Connect Card</span>
                  <ArrowRight size={16} className="billing-setup-primary-arrow" />
                </>
              )}
            </button>

            {popupBlocked && billingSetupUrl && (
              <button
                type="button"
                className="text-btn billing-setup-fallback"
                onClick={handleOpenBillingFallback}
              >
                Open payment screen
              </button>
            )}

            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;
